import React from "react";
import { formatCurrency } from "../../../../data/shared/money";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import i18n from "../../../../i18n";
import { Disclosure } from "@headlessui/react";
import {ChevronUpIcon, EyeIcon} from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faEdit } from "@fortawesome/free-regular-svg-icons";
import { faMapMarker } from "@fortawesome/free-solid-svg-icons";

const Root = ({ onClick = ()=>{}, disabled, children, Icon, displayLinker = false }) => (
    <div className="ml-1" onClick={onClick}>
        <div className={cx("relative pb-8", {
            "opacity-60 italic" : disabled
        })}>
            {displayLinker && (
                <span
                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                />
            )}
            <div className="relative flex items-start space-x-3 group-[.added]:bg-blue-100 group-[.edited]:bg-orange-100 group-[.deleted]:bg-rose-100 group-[.updated]:rounded-lg">
                <div className="relative px-1">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                        {Icon}
                    </div>
                </div>

                <div className="min-w-0 flex-1 group-[.updated]:pb-1">
                    <Disclosure defaultOpen={false}>
                        {({ open }) => (
                            <div className={cx({ "is-open group": open })}>
                                {children}
                            </div>
                        )}
                    </Disclosure>
                </div>
            </div>
        </div>
    </div>
);

const Heading = ({ children }) => (
    <Disclosure.Button
        as="div"
        className={"flex w-full cursor-pointer items-center space-x-2"}
    >
        <div className={"flex flex-col lg:mt-0 lg:flex-row lg:items-center"}>
            {children}
        </div>

        <FontAwesomeIcon
            icon={faEdit}
            className={"text-xs h-5 w-5 text-gray-500"}
        />
        <ChevronUpIcon
            className={`h-5 w-5 text-gray-500 group-[.is-open]:rotate-180 lg:!mr-16 lg:ml-0 `}
        />
    </Disclosure.Button>
);
const Title = ({ children }) => (
    <h2 className="mt-1 text-lg font-medium text-gray-900">{children}</h2>
);

const Subtitle = ({ children, hideOnOpen = true }) => (
    <>
        <div
            className={`mx-4 hidden h-[1px] w-[25px] bg-gray-300 lg:block  ${
                hideOnOpen ? "group-[.is-open]:hidden" : ""
            }`}
        ></div>
        <div
            className={`flex items-center text-xs font-medium text-gray-600 ${
                hideOnOpen ? "group-[.is-open]:hidden" : ""
            }`}
        >
            {children}
        </div>
    </>
);

const Date = ({ date, className }) => (
    <span className={"first-letter:uppercase"}>{date.format("dddd Do MMM YYYY")}</span>
);

const Quantity = ({ quantity, icon }) => (
    <div className="wb-cell quantity">
        {quantity}
        <i className={cx("fa", "fa-" + icon)} aria-hidden="true" />
    </div>
);
const SubContractor = () => (
    <div
        className="wb-cell subContractor"
        dangerouslySetInnerHTML={{ __html: i18n.t("booking.partner_provided") }}
    />
);
// const FromTo = ({ from, to }) => (
//     <div className="wb-cell main-cell transport-step">
//         <div className="start-stop">
//             {from.stop && <span className="lodging">{from.stop.title}</span>}
//             {!isEmpty(from.locality) && (
//                 <span className="location">
//                     {from.step.title} - {from.locality.title}
//                 </span>
//             )}
//             {isEmpty(from.locality) && from.step && (
//                 <span className="location">{from.step.title}</span>
//             )}
//         </div>
//
//         <div className="end-stop">
//             {to.stop && (
//                 <span className="lodging">
//                     <i className="fa fa-long-arrow-right" aria-hidden="true" />
//                     {to.stop.title}
//                 </span>
//             )}
//             {!isEmpty(to.locality) && (
//                 <span className="location">
//                     {to.step.title} - {to.locality.title}
//                 </span>
//             )}
//             {isEmpty(to.locality) && to.step && (
//                 <span className="location">{to.step.title}</span>
//             )}
//         </div>
//     </div>
// );
const Place = ({ place }) => {
    let text = !isEmpty(place.locality)
        ? `${place.step.title} - ${place.locality.title}`
        : place.step && place.step.title;

    text = text ? text : place.stop?.title;

    return <span>{text}</span>;

    // <div className="wb-cell main-cell transport-step">
    //     <div className="start-stop">
    //         {place.stop && <span className="lodging">{place.stop.title}</span>}
    //         {!isEmpty(place.locality) && (
    //             <span className="location">
    //                 {place.step.title} - {place.locality.title}
    //             </span>
    //         )}
    //         {isEmpty(place.locality) && place.step && (
    //             <span className="location">{place.step.title}</span>
    //         )}
    //     </div>
    // </div>
};
const Places = ({ place }) => (
    <div className="wb-cell fromto">
        <div className="lodging wb-cell">
            <i className="fa fa-flag" aria-hidden="true" />
            &nbsp;{place.title}
        </div>
    </div>
);
const Price = ({ price }) => (
    <div className="wb-cell price">
        <div className="inner-cell">{formatCurrency(price)}</div>
    </div>
);
const RightAction = ({ children, onClick = () => {} }) => (
    <div className="wb-cell action-cell" onClick={onClick}>
        {children}
    </div>
);
const Designation = ({ designation }) => (
    <div className="wb-cell designation">
        <div className="inner-cell">{designation}</div>
    </div>
);
const Comment = ({ comment }) => (
    <div className="wb-cell designation">
        <div className="inner-cell">{comment}</div>
    </div>
);

const Body = ({ children }) => (
    <Disclosure.Panel
        as="div"
        className="my-4 mr-2 items-center space-y-1 lg:flex lg:space-y-0 lg:space-x-4"
    >
        {children}
    </Disclosure.Panel>
);

const Info = ({ text, number, icon }) => (
    <>
        {text && (
            <div className={"flex flex-col text-center lg:mb-4"}>
                <div className={"text-lg font-semibold text-gray-700"}>{text}</div>
            </div>
        )}

        {number || icon ? (
            <div className={"text-2xl text-gray-700"}>
                {number}
                <span className={"ml-2"}>{icon}</span>
            </div>
        ) : (
            ""
        )}
    </>
);

const FromTo = ({ from, to }) => {
    return (
        <div className="space-y-5 md:min-w-0 md:flex-1 lg:mt-0 lg:flex lg:items-baseline lg:justify-between lg:!space-y-0">
            <div className="text-sm font-medium text-gray-500">
                <div className={"relative mb-4 flex items-center"}>
                    <span
                        className="absolute top-6 left-[5px] -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                    />
                    <div className="relative mr-4 flex h-3 w-3 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                        <FontAwesomeIcon
                            icon={faCircle}
                            className={"text-xs text-gray-600"}
                        />
                    </div>
                    <div>
                        {from.stop?.title ? (
                            <>
                                <b>{from.stop?.title}</b>
                                <br />
                            </>
                        ) : (
                            <div className={"flex items-center font-medium text-orange-500"}>
                                <ExclamationTriangleIcon className={`mr-1 h-5 w-5`} />
                                Aucun hébergement sélectionné
                            </div>
                        )}

                        <Place place={from} />
                    </div>
                </div>
                <div className={"flex items-center"}>
                    <div className="mr-4 flex h-3 w-3 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                        <FontAwesomeIcon
                            icon={faMapMarker}
                            className={"text-xs text-orange-500"}
                        />
                    </div>
                    <div>
                        {to.stop?.title ? (
                            <>
                                <b>{to.stop?.title}</b>
                                <br />
                            </>
                        ) : (
                            <div className={"flex items-center font-medium text-orange-500"}>
                                <ExclamationTriangleIcon className={`mr-1 h-5 w-5`} />
                                Aucun hébergement sélectionné
                            </div>
                        )}

                        <Place place={to} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export {
    Root,
    Heading,
    Title,
    Subtitle,
    Date,
    Quantity,
    SubContractor,
    FromTo,
    Price,
    Designation,
    Comment,
    RightAction,
    Place,
    Body,
    Info,
};
